import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { parseBackupFile } from '@curentia/shared';
import { ErrorText, Field, Page, Submit, passwordProblem } from '../components';
import { useAction, useDevice } from '../state';

/** Restores a backup on a device with no Curentia data, with the password or the recovery code. */
export function Restore() {
  const device = useDevice();
  const { busy, error, setError, run } = useAction();
  const [file, setFile] = useState<File | null>(null);
  const [forgot, setForgot] = useState(false);
  const [form, setForm] = useState({ password: '', code: '', newPassword: '', confirm: '' });
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!file) return setError('Choose your backup file.');
    if (forgot) {
      if (!form.code.trim()) return setError('Enter your recovery code.');
      const problem = passwordProblem(form.newPassword, form.confirm);
      if (problem) return setError(problem);
    } else if (!form.password) {
      return setError('Enter the password you used when you made the backup.');
    }
    void run(async () => {
      const backup = parseBackupFile(await file.text());
      await device.restoreBackup(backup, forgot ? { recoveryCode: form.code, newPassword: form.newPassword } : { password: form.password });
    });
  };

  return (
    <Page>
      <h1>Restore from a backup file</h1>
      <form onSubmit={submit} noValidate>
        <Field label="Backup file" type="file" accept="application/json,.json" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <label className="check">
          <input type="checkbox" checked={forgot} onChange={(e) => setForgot(e.target.checked)} />
          I forgot my password
        </label>
        {forgot ? (
          <>
            <Field label="Recovery code" autoComplete="off" value={form.code} onChange={set('code')} />
            <Field label="New password" type="password" autoComplete="new-password" value={form.newPassword} onChange={set('newPassword')} />
            <Field label="Confirm new password" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} />
          </>
        ) : (
          <Field label="Password" type="password" autoComplete="current-password" value={form.password} onChange={set('password')} />
        )}
        <ErrorText error={error} />
        <Submit busy={busy} busyText="Restoring…">
          Restore
        </Submit>
      </form>
      <div className="links">
        <Link to="/welcome">Back</Link>
      </div>
    </Page>
  );
}
